// src/App.js  (B2B-only app: remove all "/b2b" prefixes from routes, keep B2C -> ComingSoon)
import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { Box } from "@mui/material";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

// Global styles for slick
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Layout (B2B-only navbar file is recommended)
// If you already split: replace Navbar with NavbarB2B.
import Navbar from "./components/Navbar";

import AuthPage from "./components/pages/Auth/AuthPage";

// Login expired dialog
import LoginExpiredDialog from "./components/pages/Auth/LoginExpiredDialog";

// ✅ NEW: auto JWT expiry watcher
import { startJwtExpiryWatcher } from "./components/pages/Auth/jwtExpiryWatcher";

// ✅ NEW: listen to WP auth state changes (logout/expired)
import {
  wpOnAuthChanged,
  wpGetCachedUser,
  wpGetAuthSession,
  wpGetTokens,
  wpIsAuthSessionExpired,
} from "./api/wpAuth";

// Checkout (shared)
import CheckoutLayout from "./components/pages/shops/checkout/CheckoutLayout";
import ShoppingCart from "./components/pages/shops/checkout/ShoppingCart";
import AddressPage from "./components/pages/shops/checkout/AddressPage";
import PaymentPage from "./components/pages/shops/checkout/PaymentPage";
import ConfirmPage from "./components/pages/shops/checkout/ConfirmPage";

// B2B pages
import AboutShuihe from "./components/pages/About/B2B/AboutShuihe";
import HistoryShuihe from "./components/pages/About/B2B/History/HistoryShuihe";
import CompanyInformation from "./components/pages/b2b/CompanyInformation/CompanyInformation";
import Partner from "./components/pages/b2b/CompanyInformation/Partner";
import Retail from "./components/pages/b2b/Retail/Retail";
import RetailHistory from "./components/pages/b2b/Retail/RetailHistory";
import Recruit from "./components/pages/b2b/CompanyInformation/Recruit";
import NewInformation from "./components/pages/b2b/News/NewInformation";
import PressRelease from "./components/pages/b2b/News/PressRelease";
import LandingPageB2B from "./components/pages/LandingPages/b2b/LandingPageB2B";
import EventsInformation from "./components/pages/b2b/News/EventsInformation";
import HealthInformation from "./components/pages/b2b/News/HealthInformation";
import FooterB2B from "./components/Footer/FooterB2B";
import AccountPage from "./components/pages/Auth/AccountPage";
import DownloadAppRedirect from "./components/GlobalFunction/DownloadApp";
import IntroGate from "./components/IntroGate";
import ProductDetailPageB2B from "./components/pages/ProductInfo/b2b/ProductDetail/ProductDetailPageB2B";
import ProductInfoPageB2B from "./components/pages/ProductInfo/b2b/ProductInfoPageB2B";
import InquiryCartPage from "./components/pages/ProductInfo/b2b/ProductInquiry/InquiryCartPage";
import InquiryFormPage from "./components/pages/ProductInfo/b2b/ProductInquiry/InquiryFormPage";
import GlobalStickyButtons from "./components/GlobalFunction/GlobalStickyButton";
import BackToTopButton from "./components/GlobalFunction/BackToTopBotton";
import ContactUsPage from "./components/pages/ContactUs/ContactUsPage";
import PrivacyTermsPage from "./components/pages/PrivacyTermsPage";
import CookieConsentModal from "./components/CookieConsentModal";
// import FAQPageB2B from "./components/pages/Q&A/Q&AB2B";
import SiteMap from "./components/pages/sitemaps/SiteMap";
import ProductConsultForm from "./components/pages/ProductInfo/b2b/ProductConsultForm";
import { LoadingOverlay } from "./components/GlobalFunction/LoadingOverlay";
import ESGFrontPage from "./components/pages/b2b/ESG/EsgFrontPage";
import EventInformationContent from "./components/pages/b2b/News/HealthInformationContent";
import BrandStories from "./components/pages/About/B2B/BrandStories";
import ProductUniquePageB2B from "./components/pages/ProductInfo/b2b/ProductUniquePage";
import NewsCMSPage from "./components/CMS/NewsCMSPage";
import NewsContent from "./components/pages/b2b/News/NewsContent";
import InquiryHistory from "./components/pages/ProductInfo/b2b/ProductInquiry/InquiryHistory";
import { installGlobalImageLazyLoad } from "./components/GlobalFunction/installGlobalImageLazyLoad";
import Favourites from "./components/pages/ProductInfo/b2b/Favourite/Favourite";
import SearchResultsPage from "./components/GlobalFunction/SearchResultsPage";
import ComingSoon from "./components/pages/ComingSoon";
import NotFoundPage from "./components/pages/NotFoundPage";
import ResetPasswordPage from "./components/pages/Auth/ResetPasswordPage";
import GlobalImageProtection from "./components/GlobalFunction/GlobalImageProtection";
import SeoManager from "./components/GlobalFunction/SeoManager";

const fonts = {
  EN: "LINE Seed Sans",
  ZH: "LINE Seed TW",
  JP: "LINE Seed JP",
};

const HOME_ROUTE = "/首頁";
const HOME_ROUTE_ENCODED = "/%E9%A6%96%E9%A0%81";

const INTRO_GATE_ENABLE_SPLIT_CHOICE = false;

/* ------------------------ auth-route protection ------------------------ */

function isAuthRoute(pathname) {
  const p = String(pathname || "");
  return (
    p === "/auth" ||
    p.endsWith("/auth") ||
    p === "/reset-password" ||
    p.startsWith("/reset-password/")
  );
}

function isProtectedPath(pathname) {
  const p = String(pathname || "");

  // Member center / profile
  if (p === "/profile") return true;

  // Online Retail history requires login.
  // The main /線上報修 submission page remains available to guests.
  if (p === "/線上報修/紀錄") return true;

  // Inquiry flows
  if (p.includes("/詢價")) return true;
  return false;
}

/* ------------------------ B2C hard redirect ------------------------ */
function B2CToComingSoonGate() {
  const location = useLocation();
  return (
    <Navigate
      to="/comingSoon"
      replace
      state={{ from: location.pathname + location.search }}
    />
  );
}

function RedirectAuthIfLoggedIn({
  to = "/profile",
  children,
}) {
  const location = useLocation();

  const cachedUser =
    wpGetCachedUser();

  const tokens =
    wpGetTokens();

  const authSession =
    wpGetAuthSession();

  const hasUser =
    Boolean(cachedUser?.id);

  const hasAuthToken =
    Boolean(
      String(
        tokens?.authToken || "",
      )
        .replace(/^Bearer\s+/i, "")
        .trim(),
    );

  const hasRefreshToken =
    Boolean(
      String(
        tokens?.refreshToken || "",
      )
        .replace(/^Bearer\s+/i, "")
        .trim(),
    );

  const hasValidSession =
    Boolean(
      authSession &&
        !wpIsAuthSessionExpired(
          authSession,
        ),
    );

  const isFullyAuthenticated =
    hasUser &&
    hasAuthToken &&
    hasRefreshToken &&
    hasValidSession;

  if (isFullyAuthenticated) {
    return (
      <Navigate
        to={to}
        replace
        state={{
          from:
            location.pathname +
            location.search,
        }}
      />
    );
  }

  return children;
}

function AppShell() {
  const mode = "b2b";
  const location = useLocation();
  const navigate = useNavigate();

  // Login-expired dialog state
  const [loginExpiredOpen, setLoginExpiredOpen] =
    useState(false);

  /*
   * Remembers which expired session has already opened the dialog.
   *
   * This prevents repeated or delayed events from the same expired
   * session from reopening the dialog.
   */
  const expiredDialogSessionRef = useRef("");

  const TOPBAR = 36;
  const APPBAR = 64;

  function ScrollToTop() {
    const location = useLocation();

    useLayoutEffect(() => {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }

      const resetScrollPosition = () => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });

        if (document.scrollingElement) {
          document.scrollingElement.scrollTop = 0;
          document.scrollingElement.scrollLeft = 0;
        }

        document.documentElement.scrollTop = 0;
        document.documentElement.scrollLeft = 0;

        document.body.scrollTop = 0;
        document.body.scrollLeft = 0;

        const root = document.getElementById("root");
        if (root) {
          root.scrollTop = 0;
          root.scrollLeft = 0;
        }

        const main = document.querySelector("main");
        if (main) {
          main.scrollTop = 0;
          main.scrollLeft = 0;
        }
      };

      resetScrollPosition();

      const animationFrameOne = window.requestAnimationFrame(
        resetScrollPosition,
      );

      const animationFrameTwo = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(resetScrollPosition);
      });

      const shortDelay = window.setTimeout(resetScrollPosition, 60);
      const longDelay = window.setTimeout(resetScrollPosition, 180);

      return () => {
        window.cancelAnimationFrame(animationFrameOne);
        window.cancelAnimationFrame(animationFrameTwo);
        window.clearTimeout(shortDelay);
        window.clearTimeout(longDelay);
      };
    }, [location.pathname, location.search, location.key]);

    return null;
  }

  const rawPathname = String(location.pathname || "");
  const decodedPathname = (() => {
    try {
      return decodeURIComponent(rawPathname);
    } catch (e) {
      return rawPathname;
    }
  })();

  // Intro gating
  const isRoot = rawPathname === "/" || rawPathname === "";
  const isBypassHome =
    decodedPathname === HOME_ROUTE || rawPathname === HOME_ROUTE_ENCODED;
  const hasSeenIntro = !!sessionStorage.getItem("introSeen");

  // If user opens a deep link directly (not "/"), treat intro as already seen
  useEffect(() => {
    if (!hasSeenIntro && !isRoot) {
      sessionStorage.setItem("introSeen", "1");
    }
  }, [hasSeenIntro, isRoot]);

  const isLanding = isBypassHome;
  const shouldAutoShowCookie = hasSeenIntro && isLanding;

  // ComingSoon page flag (case-insensitive for /comingsoon or /comingSoon)
  const isComingSoon = /^\/comingsoon$/i.test(rawPathname);

  /**
   * ✅ IMPORTANT:
   * Do NOT show expired dialog for user-initiated logout.
   * We'll ignore auth change events with reason "user" / "logout".
   */
  const isUserInitiatedLogoutReason = (reasonRaw) => {
    const r = String(reasonRaw || "")
      .toLowerCase()
      .trim();
    return (
      r === "user" ||
      r === "logout" ||
      r === "manual" ||
      r === "user_logout" ||
      r === "user-logout"
    );
  };

  const openExpiredDialog = useCallback(
    (sessionId = "") => {
      /*
       * Do not show the dialog while already on an authentication
       * or password-reset page.
       */
      if (isAuthRoute(rawPathname)) {
        return;
      }

      const normalizedSessionId = String(
        sessionId || "",
      ).trim();

      /*
       * Unscoped events use their own key so repeated invalid-session
       * events also cannot continuously reopen the dialog.
       */
      const dialogSessionKey =
        normalizedSessionId || "__unscoped__";

      if (
        expiredDialogSessionRef.current ===
        dialogSessionKey
      ) {
        return;
      }

      expiredDialogSessionRef.current =
        dialogSessionKey;

      try {
        localStorage.setItem(
          "authReturnTo",
          rawPathname + location.search,
        );
      } catch (error) {
        // Browser storage may be unavailable.
      }

      setLoginExpiredOpen(true);
    },
    [rawPathname, location.search],
  );

  /*
   * Listen to the single authentication event path emitted by
   * wpAuth.js.
   *
   * Events from an older session must not affect a newer login.
   */
  useEffect(() => {
    const unsubscribe = wpOnAuthChanged(
      (detail) => {
        const eventDetail =
          detail &&
          typeof detail === "object"
            ? detail
            : {};

        const pathname =
          decodedPathname;

        const reason = String(
          eventDetail?.reason || "",
        )
          .toLowerCase()
          .trim();

        const eventSessionId = String(
          eventDetail?.sessionId || "",
        ).trim();

        const currentSession =
          wpGetAuthSession();

        const currentSessionId = String(
          currentSession?.sessionId || "",
        ).trim();

        const user =
          eventDetail?.user ?? null;

        const hasToken =
          eventDetail?.hasToken === true;

        /*
         * Ignore an event explicitly belonging to an older session.
         *
         * Example:
         * Session A expires after Session B has already logged in.
         */
        if (
          eventSessionId &&
          currentSessionId &&
          eventSessionId !==
            currentSessionId
        ) {
          return;
        }

        /*
         * An unscoped logged-out event must not override a complete
         * active session.
         */
        if (
          !eventSessionId &&
          currentSessionId &&
          !hasToken
        ) {
          return;
        }

        /*
         * A successful login or refresh confirms that the browser
         * now has an active session.
         *
         * Close any expiration dialog belonging to the previous
         * session.
         */
        if (
          hasToken &&
          currentSessionId &&
          (
            !eventSessionId ||
            eventSessionId ===
              currentSessionId
          )
        ) {
          if (
            reason === "login" ||
            reason === "refresh"
          ) {
            expiredDialogSessionRef.current =
              "";

            setLoginExpiredOpen(false);
          }

          return;
        }

        /*
         * Manual logout must not show the expired-session dialog.
         */
        if (
          isUserInitiatedLogoutReason(
            reason,
          )
        ) {
          return;
        }

        if (isAuthRoute(pathname)) {
          return;
        }

        if (
          !isProtectedPath(pathname)
        ) {
          return;
        }

        if (reason === "expired") {
          openExpiredDialog(
            eventSessionId,
          );

          return;
        }

        /*
         * These are ordinary login or startup transitions, not an
         * expired authenticated session.
         */
        if (
          reason === "login_start" ||
          reason === "login_failed" ||
          reason ===
            "bootstrap_no_session"
        ) {
          return;
        }

        /*
         * A force-cleared or incomplete current session should also
         * use the login-expired dialog on protected pages.
         */
        if (!user && !hasToken) {
          openExpiredDialog(
            eventSessionId,
          );
        }
      },
    );

    return () => {
      unsubscribe?.();
    };
  }, [
    decodedPathname,
    openExpiredDialog,
  ]);

  /*
   * Extra safety for users who directly open a protected page.
   *
   * Cached profile data alone is not proof of authentication.
   * The complete token pair and unexpired browser session must exist.
   */
  useEffect(() => {
    if (
      isAuthRoute(decodedPathname)
    ) {
      return;
    }

    if (
      !isProtectedPath(
        decodedPathname,
      )
    ) {
      return;
    }

    const cachedUser =
      wpGetCachedUser();

    const tokens =
      wpGetTokens();

    const authSession =
      wpGetAuthSession();

    const hasViewer =
      Boolean(cachedUser?.id);

    const hasAuthToken =
      Boolean(
        String(
          tokens?.authToken || "",
        )
          .replace(
            /^Bearer\s+/i,
            "",
          )
          .trim(),
      );

    const hasRefreshToken =
      Boolean(
        String(
          tokens?.refreshToken || "",
        )
          .replace(
            /^Bearer\s+/i,
            "",
          )
          .trim(),
      );

    const hasValidSession =
      Boolean(
        authSession &&
          !wpIsAuthSessionExpired(
            authSession,
          ),
      );

    const isFullyAuthenticated =
      hasViewer &&
      hasAuthToken &&
      hasRefreshToken &&
      hasValidSession;

    if (!isFullyAuthenticated) {
      openExpiredDialog(
        authSession?.sessionId ||
          "",
      );
    }
  }, [
    decodedPathname,
    openExpiredDialog,
  ]);

  // Watch the current browser session's fixed expiration deadline.
  useEffect(() => {
    const cleanup =
      startJwtExpiryWatcher({
        safetyIntervalMs: 15000,
      });

    return cleanup;
  }, []);

  // ✅ normalize encoded home path to visible Chinese path
  useEffect(() => {
    if (rawPathname === HOME_ROUTE_ENCODED) {
      navigate(HOME_ROUTE, { replace: true });
    }
  }, [rawPathname, navigate]);

  const goToAuth = useCallback(() => {
    setLoginExpiredOpen(false);
    navigate(`/auth`, { replace: true });
  }, [navigate]);

  const closeDialog = useCallback(() => {
    setLoginExpiredOpen(false);
    navigate(`/auth`, { replace: true });
  }, [navigate]);

  // When IntroGate is visible, still render SeoManager so the root URL
  // has the correct SEO title, description, canonical URL, and JSON-LD.
  if (isRoot && !hasSeenIntro) {
    return (
      <>
        <SeoManager />

        <IntroGate
          b2cTo="/comingSoon"
          b2bTo={HOME_ROUTE}
          enableSplitChoice={INTRO_GATE_ENABLE_SPLIT_CHOICE}
        />
      </>
    );
  }

  return (
    <>
      {/* ✅ Login expired dialog at the very top */}
      <LoginExpiredDialog
        open={loginExpiredOpen}
        mode={mode}
        onConfirm={goToAuth}
        onClose={closeDialog}
      />

      <GlobalImageProtection />

      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fffffc",
        }}
      >
        <SeoManager />

        <ScrollToTop />

        {/* Cookie consent – hidden on ComingSoon page */}
        {!isComingSoon && (
          <CookieConsentModal
            key={mode}
            mode={mode}
            policyHref={`/隱私權`}
            version={1}
            autoShow={shouldAutoShowCookie}
            triggerEventName="app:openCookieSettings"
          />
        )}

        {/* Navbar – hidden on ComingSoon page */}
        {!isComingSoon && <Navbar mode={mode} setMode={() => {}} />}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: {
              xs: `calc(100dvh - ${APPBAR}px)`,
              md: `calc(100dvh - ${APPBAR + TOPBAR}px)`,
            },
          }}
        >
          <Routes>
            {/* ✅ B2C hard redirect */}
            <Route path="/b2c" element={<B2CToComingSoonGate />} />
            <Route path="/b2c/*" element={<B2CToComingSoonGate />} />

            {/* ✅ ComingSoon restored */}
            <Route path="/comingSoon" element={<ComingSoon mode="b2c" />} />

            {/* ✅ Root after intro seen => always go to 首頁 */}
            <Route path="/" element={<Navigate to={HOME_ROUTE} replace />} />

            {/* ✅ support encoded route too */}
            <Route
              path={HOME_ROUTE_ENCODED}
              element={<Navigate to={HOME_ROUTE} replace />}
            />

            {/* ✅ actual landing page */}
            <Route path={HOME_ROUTE} element={<LandingPageB2B mode="b2b" />} />

            <Route path="/download-app" element={<DownloadAppRedirect />} />

            {/* ============================= B2B (no /b2b prefix) ============================== */}
            <Route
              path="/關於水禾"
              element={<AboutShuihe mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/企業責任"
              element={<ESGFrontPage mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/品牌資訊"
              element={<BrandStories mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/公司簡介"
              element={<HistoryShuihe mode="b2b" fonst={fonts} />}
            />
            <Route
              path="/合作資訊"
              element={<CompanyInformation mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/聯絡水禾"
              element={<ContactUsPage mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/合作資訊/技術合作及聯合開發諮詢"
              element={<Partner mode="b2b" fonts={fonts} />}
            />

            <Route
              path="/線上報修"
              element={<Retail mode="b2b" fonts={fonts} />}
            />

            <Route
              path="/線上報修/紀錄"
              element={<RetailHistory fonts={fonts} />}
            />

            <Route
              path="/合作資訊/招募訊息"
              element={<Recruit mode="b2b" fonts={fonts} />}
            />

            {/* 最新資訊 */}

            {/* <Route
              path="/最新資訊CMS/"
              element={<NewsCMSPage mode="b2b" fonts={fonts} />}
            /> */}
            <Route
              path="/最新資訊/"
              element={<NewInformation mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/最新資訊/最新消息/"
              element={<PressRelease mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/最新資訊/活動資訊/"
              element={<EventsInformation mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/最新資訊/衛生資訊/"
              element={<HealthInformation mode="b2b" fonts={fonts} />}
            />

            <Route
              path="/最新資訊/衛生資訊/:slug"
              element={<EventInformationContent mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/最新資訊/最新消息/:slug"
              element={<NewsContent mode="b2b" fonts={fonts} />}
            />

            <Route
              path="/auth"
              element={
                <RedirectAuthIfLoggedIn to="/profile">
                  <AuthPage mode="b2b" fonts={fonts} />
                </RedirectAuthIfLoggedIn>
              }
            />

            <Route
              path="/reset-password/:token"
              element={<ResetPasswordPage mode="b2b" fonts={fonts} />}
            />

            <Route
              path="/reset-password"
              element={<Navigate to="/auth" replace />}
            />

            <Route path="/checkout" element={<CheckoutLayout fonts={fonts} />}>
              <Route path="cart" element={<ShoppingCart fonts={fonts} />} />
              <Route path="address" element={<AddressPage fonts={fonts} />} />
              <Route path="payment" element={<PaymentPage fonts={fonts} />} />
              <Route path="confirm" element={<ConfirmPage fonts={fonts} />} />
            </Route>

            <Route
              path="/商品資訊"
              element={<ProductInfoPageB2B mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/商品資訊/:id"
              element={<ProductDetailPageB2B mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/我的收藏"
              element={<Favourites mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/商品資訊/專業諮詢(專人服務)"
              element={<ProductConsultForm mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/商品資訊/unique"
              element={<ProductUniquePageB2B fonts={fonts} />}
            />

            <Route path="/CMS/最新資訊" element={<NewsCMSPage />} />

            <Route
              path="/profile"
              element={<AccountPage mode="b2b" fonts={fonts} />}
            />

            {/* <Route path="/常見問題Q&A" element={<FAQPageB2B fonts={fonts} />} /> */}

            <Route path="/詢價" element={<InquiryCartPage fonts={fonts} />} />
            <Route
              path="/詢價/查看"
              element={<InquiryFormPage fonts={fonts} />}
            />
            <Route
              path="/詢價/歷史"
              element={<InquiryHistory fonts={fonts} />}
            />

            <Route
              path="/隱私權"
              element={<PrivacyTermsPage mode="b2b" fonts={fonts} />}
            />
            <Route
              path="/網站地圖"
              element={<SiteMap page="sitemap" mode="b2b" fonts={fonts} />}
            />

            <Route path="/搜尋" element={<SearchResultsPage fonts={fonts} />} />

            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Box>

        {/* Sticky + footer hidden on ComingSoon (optional but consistent with your old behavior) */}
        {!isComingSoon && (
          <>
            <GlobalStickyButtons hideOnSelector="footer" />
            <BackToTopButton mode={mode} />
          </>
        )}
        {!isComingSoon && (
          <FooterB2B mode={"b2b"} setMode={() => {}} fonts={fonts} />
        )}
      </Box>
    </>
  );
}

export default function App() {
  useEffect(() => {
    installGlobalImageLazyLoad();
  }, []);

  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  return (
    <LoadingOverlay cancelable>
      <AppShell />
    </LoadingOverlay>
  );
}
